import { useState, useEffect, FC } from 'react'
import dynamic from 'next/dynamic'
import cn from 'clsx'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/router'
import type { Page } from '@commerce/types/page'
import getSlug from '@lib/get-slug'
import Container from '@components/ui/Container/Container'
import { useUI } from '@components/ui/context'
import FooterYdCollapse from '@components/yadav/ui/YdCollapse/FooterYdCollapse'
import footerData from '@components/common/Footer/footerNavContent.json'
import s from './Footer.module.css'
//import Head from 'next/head'
import rating from './rating.svg'

const SocialMediaNav = dynamic(
  () => import('@components/yadav/common/SocialMediaNav')
)
const NewsletterForm = dynamic(
  () => import('@components/yadav/ui/NewsletterForm')
)
const YdLogos = dynamic(() => import('@components/yadav/ui/YdLogos'), {
  loading: () => <p className="py-10 text-center"></p>,
})

const YdCustomerReviews = dynamic(
  () => import('@components/yadav/ui/YdCustomerReviews')
)
interface Props {
  className?: string
  children?: any
  pages?: Page[]
}

const Footer: FC<Props> = ({ className, pages }) => {
  const [currentYear, setCurrentYear] = useState<number | undefined>()
  const rootClassName = cn(s.root, className)
  const { openModal, setModalView } = useUI()
  const [invalidPage, setInvalidPage] = useState<boolean>(false)
  const [ydLogos, setYdLogos] = useState<boolean>(true)
  const router = useRouter()
  
  useEffect(() => {
    const invalidPages = [
      '/hs-bands',
      '/hs-engrings',
      '/hs-jewelry',
      '/hs-service',
      '/hsen-d1',
    ]
    // here we filter out any pages that are not valid for newsletter
    if (invalidPages.includes(window.location.pathname)) {
      setInvalidPage(true)
    } else {
      setInvalidPage(false)
    }

    const noYdLogosPages = [
      '/virtual-consultation',
    ]
    // Here we filter out pages that do not require YdLogos
    if (noYdLogosPages.includes(window.location.pathname)) {
      setYdLogos(false)
    } else {
      setYdLogos(true)
    }
  }, [router])

  const handleClickSecure = () => {
    setModalView('SECURE_VIEW')
    openModal()
  }

  useEffect(() => {
    setCurrentYear(new Date().getFullYear())
  }, [])

  const handleNewsLetterLoad = () => {
    // if actual page is invalid we dont render newsletter
    if (invalidPage) {
      return
    } else {
      return <NewsletterForm />
    }
  }
  return (
    <footer>
      <div className="mlg:hidden block">
        {ydLogos && <YdLogos />}
      </div>
      {/* <div className="hidden">
        <YdCustomerReviews />
      </div> */}
      {handleNewsLetterLoad()}
      <div className={rootClassName}>
        <Container clean className={s.container}>
          <ul className="block lg:flex">
            <li className="md:hidden">
              {footerData.map((d) => (
                <FooterYdCollapse
                  key={d.header}
                  title={d.header}
                  headerPos="center"
                  headerStyle="text-primary font-semibold text-base hover:text-primary"
                >
                  <ul className={s.dropdownList}>
                    {d.links.map((l) => (
                      <li key={l.label}>
                        {l.href === 'null' ? (
                          <span>{l.label}</span>
                        ) : (
                          <Link role="link" href={l.href}>
                            <a aria-label={l.label}>{l.label}</a>
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </FooterYdCollapse>
              ))}
              <nav className="footer-yadav-nav mt-6 flex justify-center">
                <SocialMediaNav className="mb-3 text-center md:text-left" />
              </nav>
            </li>
            <li className="hidden md:flex md:justify-between lg:justify-evenly lg:flex-1 lg:gap-3">
              {footerData.map((d) => (
                <div key={d.header}>
                  <h1 className="capitalize mb-5 font-bold text-primary text-xl">
                    {d.header}
                  </h1>
                  <ul className="text-sm">
                    {d.links.map((l) => (
                      <li key={l.label} className="mb-2 last:mb-0">
                        {l.href === 'null' ? (
                          <>
                            <span className=" text-gray-dark text-base">
                              {l.label}
                            </span>
                          </>
                        ) : (
                          <>
                            <div className="flex flex-row items-center">
                              <Link href={l.href}>
                                <a
                                  className={
                                    l.label === 'YADAV Reviews'
                                      ? 'font-semibold hover:underline text-gray-dark text-base mr-2'
                                      : 'hover:underline text-gray-dark text-base'
                                  }
                                >
                                  {l.label}
                                </a>
                              </Link>
                              {l.label === 'YADAV Reviews' && (
                                <Image
                                  src={rating}
                                  alt="Yadav Jewelry Reviews"
                                  width={72}
                                  height={15}
                                />
                              )}
                            </div>
                            {l.label === 'Contact Us' && (
                              <nav className="footer-yadav-nav mt-6">
                                <SocialMediaNav className="mb-3 text-center md:text-left" />
                              </nav>
                            )}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </li>
          </ul>
          <div className="block md:flex md:justify-between md:items-center lg:mt-2"></div>
        </Container>

        <div>
          <span className={s.copy}>
            &copy; 1999 to {currentYear} Yadav Diamonds and Jewelry. All Rights
            Reserved.
          </span>
        </div>
      </div>
    </footer>
  )
}

function usePages(pages?: Page[]) {
  const { locale } = useRouter()
  const sitePages: Page[] = []

  if (pages) {
    pages.forEach((page) => {
      const slug = page.url && getSlug(page.url)
      if (!slug) return
      if (locale && !slug.startsWith(`${locale}/`)) return
      sitePages.push(page)
    })
  }

  return {
    sitePages: sitePages.sort(bySortOrder),
  }
}

// Sort pages by the sort order assigned in the BC dashboard
function bySortOrder(a: Page, b: Page) {
  return (a.sort_order ?? 0) - (b.sort_order ?? 0)
}

export default Footer

